import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Send, Phone, Circle, Image as ImageIcon, FileText, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

// URL do Venom-Bot
const VENOM_BOT_URL = process.env.REACT_APP_VENOM_BOT_URL || 'http://localhost:3000';

export default function WhatsApp() {
  const [chats, setChats] = useState([]);
  const [chatSelecionado, setChatSelecionado] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingChats, setLoadingChats] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const socketRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Conectar WebSocket para mensagens em tempo real
    const socket = io(VENOM_BOT_URL);
    socketRef.current = socket;
    
    socket.on('connect', () => {
      console.log('🔌 WebSocket conectado ao WhatsApp');
    });
    
    socket.on('status', (data) => {
      console.log('📊 Status WhatsApp:', data.status);
      setConnectionStatus(data.status);
      
      // Se conectou, carregar chats
      if (data.status === 'connected') {
        carregarChats();
      }
    });
    
    socket.on('message', (data) => {
      console.log('📩 Nova mensagem via WebSocket:', data);
      
      // Adicionar mensagem em tempo real se for do chat selecionado
      if (chatSelecionado && (data.from === chatSelecionado.id || data.to === chatSelecionado.id)) {
        setMensagens(prev => {
          // Verificar se mensagem já existe
          const exists = prev.some(msg => msg.id === data.id);
          if (exists) return prev;
          
          return [...prev, {
            id: data.id,
            body: data.body,
            timestamp: data.timestamp,
            fromMe: data.to === chatSelecionado.id, // Se enviou para o chat, é fromMe
            hasMedia: data.hasMedia,
            type: data.type
          }];
        });
      }
      
      // Atualizar lista de chats (sem fazer request, apenas reordenar)
      setChats(prev => {
        const updated = [...prev];
        updated.sort((a, b) => b.timestamp - a.timestamp);
        return updated;
      });
    });
    
    // Verificar status inicial
    verificarStatus();
    
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [chatSelecionado]); // Adicionar chatSelecionado como dependência

  useEffect(() => {
    if (chatSelecionado) {
      carregarMensagens(chatSelecionado.id);
    }
  }, [chatSelecionado]);
  
  // Auto-scroll para última mensagem
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [mensagens]);

  const verificarStatus = async () => {
    try {
      const res = await axios.get(`${VENOM_BOT_URL}/status`);
      setConnectionStatus(res.data.status);
      
      if (res.data.status === 'connected') {
        carregarChats();
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    }
  };

  const carregarChats = async () => {
    if (connectionStatus !== 'connected') return;
    
    setLoadingChats(true);
    try {
      const res = await axios.get(`${VENOM_BOT_URL}/chats`);
      setChats(res.data);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao carregar conversas';
      toast.error(`Erro: ${errorMsg}`);
      
      // Se for erro de conexão, pode ser que o WhatsApp tenha desconectado
      if (error.response?.status === 400) {
        setConnectionStatus('disconnected');
      }
    } finally {
      setLoadingChats(false);
    }
  };

  const carregarMensagens = async (chatId) => {
    try {
      const res = await axios.get(`${VENOM_BOT_URL}/chats/${chatId}/messages?limit=100`);
      // Mensagens já vêm na ordem correta (mais antigas primeiro)
      setMensagens(res.data);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Erro ao carregar mensagens';
      toast.error(`Erro: ${errorMsg}`);
      
      // Se for erro de conexão, pode ser que o WhatsApp tenha desconectado
      if (error.response?.status === 400) {
        setConnectionStatus('disconnected');
      }
    }
  };

  const enviarMensagem = async () => {
    if (!novaMensagem.trim() || !chatSelecionado) return;

    setLoading(true);
    try {
      await axios.post(`${VENOM_BOT_URL}/send`, {
        to: chatSelecionado.id,
        message: novaMensagem
      });
      
      // Adicionar mensagem localmente
      const novaMensagemObj = {
        id: `temp-${Date.now()}`,
        body: novaMensagem,
        timestamp: Math.floor(Date.now() / 1000),
        fromMe: true,
        hasMedia: false,
        type: 'chat'
      };
      
      setMensagens(prev => [...prev, novaMensagemObj]);
      setNovaMensagem('');
      toast.success('Mensagem enviada!');
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
      case 'qr_ready':
        return 'bg-yellow-500';
      default:
        return 'bg-red-500';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'WhatsApp Online';
      case 'connecting':
        return 'Conectando...';
      case 'qr_ready':
        return 'Aguardando QR Code';
      default:
        return 'WhatsApp Offline';
    }
  };

  return (
    <div data-testid="whatsapp-page">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">WhatsApp</h1>
            <p className="text-slate-600">Converse com os clientes diretamente pelo sistema</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
            <Circle className={`w-3 h-3 ${getStatusColor()} animate-pulse`} fill="currentColor" />
            <span className="text-sm font-medium text-slate-700">{getStatusText()}</span>
          </div>
        </div>
      </div>

      {connectionStatus !== 'connected' && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800">
            ⚠️ WhatsApp não está conectado. 
            <a href="/whatsapp-connection" className="underline ml-2 font-semibold">
              Clique aqui para conectar
            </a>
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Conversas */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Conversas</CardTitle>
              <Button 
                onClick={carregarChats} 
                disabled={connectionStatus !== 'connected' || loadingChats}
                size="sm"
                variant="outline"
              >
                {loadingChats ? 'Carregando...' : 'Atualizar'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px] pr-4">
              {chats.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">
                  {connectionStatus !== 'connected' 
                    ? 'WhatsApp não conectado'
                    : 'Nenhuma conversa encontrada'}
                </p>
              ) : (
                <div className="space-y-2">
                  {chats.map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => setChatSelecionado(chat)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        chatSelecionado?.id === chat.id
                          ? 'bg-blue-100 border border-blue-300'
                          : 'hover:bg-slate-100 border border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {chat.profilePicUrl ? (
                          <img 
                            src={chat.profilePicUrl} 
                            alt={chat.name}
                            className="w-10 h-10 rounded-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="bg-green-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-semibold text-sm"
                          style={{ display: chat.profilePicUrl ? 'none' : 'flex' }}
                        >
                          {chat.name ? chat.name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-800 truncate">{chat.name || 'Sem nome'}</p>
                            {chat.unreadCount > 0 && (
                              <span className="bg-green-500 text-white text-xs rounded-full px-2 py-0.5">
                                {chat.unreadCount}
                              </span>
                            )}
                          </div>
                          {chat.lastMessage && (
                            <p className="text-sm text-slate-500 truncate">
                              {chat.lastMessage.fromMe ? 'Você: ' : ''}
                              {chat.lastMessage.body || '📎 Mídia'}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat */}
        <Card className="lg:col-span-2">
          {chatSelecionado ? (
            <>
              <CardHeader className="border-b">
                <div className="flex items-center gap-3">
                  {chatSelecionado.profilePicUrl ? (
                    <img 
                      src={chatSelecionado.profilePicUrl} 
                      alt={chatSelecionado.name}
                      className="w-12 h-12 rounded-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="bg-green-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-semibold text-lg"
                    style={{ display: chatSelecionado.profilePicUrl ? 'none' : 'flex' }}
                  >
                    {chatSelecionado.name ? chatSelecionado.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{chatSelecionado.name || 'Sem nome'}</CardTitle>
                    <p className="text-sm text-slate-500">
                      {chatSelecionado.isGroup ? '👥 Grupo' : '💬 Conversa'}
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[480px] p-4" ref={scrollRef}>
                  {mensagens.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">Nenhuma mensagem ainda</p>
                  ) : (
                    <div className="space-y-3">
                      {mensagens.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${
                            msg.fromMe ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg px-4 py-2 ${
                              msg.fromMe
                                ? 'bg-green-600 text-white'
                                : 'bg-slate-200 text-slate-800'
                            }`}
                          >
                            {msg.hasMedia && msg.mediaData ? (
                              <div className="mb-2">
                                {msg.mediaData.mimetype.startsWith('image/') ? (
                                  <img 
                                    src={`data:${msg.mediaData.mimetype};base64,${msg.mediaData.data}`}
                                    alt="Imagem"
                                    className="max-w-full rounded"
                                  />
                                ) : (
                                  <div className="flex items-center gap-2 p-2 bg-white bg-opacity-20 rounded">
                                    <FileText size={20} />
                                    <span className="text-sm">{msg.mediaData.filename || 'Arquivo'}</span>
                                  </div>
                                )}
                              </div>
                            ) : null}
                            {msg.body && <p className="text-sm whitespace-pre-wrap">{msg.body}</p>}
                            <p
                              className={`text-xs mt-1 ${
                                msg.fromMe ? 'text-green-100' : 'text-slate-500'
                              }`}
                            >
                              {new Date(msg.timestamp * 1000).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      value={novaMensagem}
                      onChange={(e) => setNovaMensagem(e.target.value)}
                      placeholder="Digite sua mensagem..."
                      onKeyPress={(e) => e.key === 'Enter' && enviarMensagem()}
                      data-testid="input-mensagem"
                    />
                    <Button
                      onClick={enviarMensagem}
                      disabled={loading || !novaMensagem.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="enviar-mensagem-btn"
                    >
                      <Send size={18} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </>
          ) : (
            <CardContent className="flex flex-col items-center justify-center h-[600px] gap-4">
              <div className="text-6xl">💬</div>
              <p className="text-slate-500 text-lg">Selecione uma conversa para iniciar</p>
              {connectionStatus !== 'connected' && (
                <a href="/whatsapp-connection" className="text-blue-600 underline">
                  Conectar WhatsApp
                </a>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}